import { cn } from '@/lib/shadcn/utils';
import type {
  CandidateExperienceApi,
  CandidatePathParams,
} from '@/modules/candidate-detail/queries/types';
import { useCandidateExperienceSuspenseQuery } from '@/modules/candidate-detail/queries/useCandidateExperienceQuery';
import { getTotalExperience } from '@/modules/candidate-detail/utils/candidateProfile';
import { sortRolesNewestFirst } from '@/modules/candidate-detail/utils/sortRolesNewestFirst';
import { parseDateParam } from '@/utils/dateUtils';

interface CandidateExperienceProps {
  params: CandidatePathParams;
}

/** Pixels of rail per year of tenure. */
const PX_PER_YEAR = 26;
const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;
/** Current roles run to now; a module constant keeps rendering pure. */
const NOW_YEARS = Date.now() / MS_PER_YEAR + 1970;

const toYears = (value: string | null, fallback: number) => {
  const date = parseDateParam(value);
  return date ? date.getTime() / MS_PER_YEAR + 1970 : fallback;
};

const yearLabel = (value: string | null) =>
  parseDateParam(value)?.getFullYear().toString();

const durationLabel = (years: number) => {
  const rounded = Math.round(years * 2) / 2;
  if (rounded < 1) return `${Math.max(1, Math.round(years * 12))} mo`;
  return rounded === 1 ? '1 yr' : `${rounded} yrs`;
};

/**
 * Vertical rail: each role a segment whose height scales with its tenure,
 * company named once, the current role anchored on top with a live dot.
 */
export function CandidateExperience({ params }: CandidateExperienceProps) {
  const experienceQuery = useCandidateExperienceSuspenseQuery(params);
  const roles = sortRolesNewestFirst(experienceQuery.data);

  if (roles.length === 0) {
    return (
      <p className="text-muted-foreground border-y py-4 text-[13px]">
        Work experience has not been added for this candidate.
      </p>
    );
  }

  const total = getTotalExperience(roles);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-baseline justify-between text-[13px]">
        <span className="font-semibold">
          {total ? `${total} of experience` : 'Career history'}
        </span>
        <span className="text-neutral-500">Segment height is tenure</span>
      </div>
      <ol className="flex flex-col">
        {roles.map((role, index) => (
          <ExperienceSegment
            isNewCompany={
              index === 0 || roles[index - 1]?.companyName !== role.companyName
            }
            key={`${role.companyName}-${role.jobTitle}-${index}`}
            role={role}
          />
        ))}
      </ol>
    </div>
  );
}

function ExperienceSegment({
  isNewCompany,
  role,
}: {
  isNewCompany: boolean;
  role: CandidateExperienceApi;
}) {
  const isCurrent = !role.endDate;
  const start = toYears(role.startDate, NOW_YEARS);
  const end = isCurrent ? NOW_YEARS : toYears(role.endDate, start);
  const years = Math.max(end - start, 0);
  const startLabel = yearLabel(role.startDate);
  const endLabel = isCurrent ? 'now' : yearLabel(role.endDate);
  const span =
    startLabel && endLabel ? `${startLabel} – ${endLabel}` : undefined;
  const achievements = role.achievements.filter(Boolean);

  return (
    <li
      className="grid grid-cols-[16px_minmax(0,1fr)] gap-x-5"
      style={{ minHeight: years * PX_PER_YEAR + 40 }}
    >
      <span className="relative flex flex-col items-center">
        <span
          className={cn(
            'z-[1] mt-1.5 size-2.5 shrink-0 rounded-full',
            isCurrent
              ? 'bg-semantic-560 ring-semantic-560/15 ring-4'
              : 'border-input bg-background border-2',
          )}
        />
        <span
          className={cn('w-0.5 flex-1', isCurrent ? 'bg-input' : 'bg-border')}
        />
      </span>
      <div className="flex flex-col gap-1.5 pb-6">
        {isNewCompany && (
          <span className="text-[11px] font-semibold tracking-wide text-neutral-500 uppercase">
            {role.companyName}
          </span>
        )}
        <p className="font-medium">
          {role.jobTitle}
          <span className="font-normal text-neutral-500 tabular-nums">
            {span && ` · ${span}`}
            {role.startDate && ` · ${durationLabel(years)}`}
          </span>
        </p>
        {achievements.map((achievement, index) => (
          <p
            className="text-muted-foreground max-w-[64ch] text-[13px] leading-[18px]"
            key={`${achievement}-${index}`}
          >
            {achievement}
          </p>
        ))}
      </div>
    </li>
  );
}
